/**
 * Minimal IndexedDB wrapper for offline storage.
 * Supports sync_queue and plans stores.
 * No external dependencies.
 */

const DB_NAME = 'velogistique_offline';
const DB_VERSION = 2;
export const STORE_NAME = 'sync_queue';
export const PLANS_STORE = 'plans';

/** @type {IDBDatabase|null} */
let db = null;

/**
 * Open (or create) the IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
export function openDB() {
  if (db) return Promise.resolve(db);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      // V1: sync_queue store
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('type', 'type', { unique: false });
      }
      // V2: plans store for offline plan caching
      if (!database.objectStoreNames.contains(PLANS_STORE)) {
        database.createObjectStore(PLANS_STORE, { keyPath: 'planId' });
      }
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      resolve(db);
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Execute a transaction on a specific store
 * @param {string} storeName - Store to operate on
 * @param {'readonly'|'readwrite'} mode
 * @param {(store: IDBObjectStore) => IDBRequest} callback
 * @returns {Promise<any>}
 */
export async function withNamedStore(storeName, mode, callback) {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const request = callback(store);
    if (request) {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }
    tx.oncomplete = () => { if (!request) resolve(undefined); };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Execute a transaction on the sync queue store (backwards compatible)
 * @param {'readonly'|'readwrite'} mode
 * @param {(store: IDBObjectStore) => IDBRequest} callback
 * @returns {Promise<any>}
 */
export async function withStore(mode, callback) {
  return withNamedStore(STORE_NAME, mode, callback);
}

/**
 * Save a delivery plan + stops to IndexedDB for offline access
 * @param {string} planId
 * @param {object} plan
 * @param {Array<object>} stops
 * @returns {Promise<void>}
 */
export async function cachePlan(planId, plan, stops) {
  return withNamedStore(PLANS_STORE, 'readwrite', (store) =>
    store.put({ planId, plan, stops, cachedAt: new Date().toISOString() })
  );
}

/**
 * Get a cached delivery plan from IndexedDB
 * @param {string} planId
 * @returns {Promise<{plan: object, stops: Array<object>, cachedAt: string}|null>}
 */
export async function getCachedPlan(planId) {
  const result = await withNamedStore(PLANS_STORE, 'readonly', (store) =>
    store.get(planId)
  );
  return result || null;
}
